import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { log } from '@execonline-inc/logging';
import { assertNever, noop } from '@kofno/piper';
import { Kettle } from '@execonline-inc/kettle.private';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { useEffect, useState, type JSX } from 'react';
import { selectPlayer } from './PlayerSelection';
import ReqHlsRenderedPlayer from './ReqHlsRenderedPlayer';
import { ReqHlsVideoAssetResource, SelectedPlayer, VideoSelectionFailure } from './Types';

export interface Props {
  id: string;
  className: string;
  videoResource: ReqHlsVideoAssetResource;
  kettle: Kettle;
  width: string | number;
  height: string | number;
  dynamicallySetAspectRatio?: boolean;
  children: {
    playButton: Maybe<JSX.Element>;
  };
}

const logFailure =
  (videoResource: ReqHlsVideoAssetResource) =>
  (failure: VideoSelectionFailure): void => {
    switch (failure.kind) {
      case 'missing-progressive-low':
        warnAndNotify(
          'ProgressiveLowRenderingFailure',
          `Low progressive stream is missing from the video asset ${videoResource.payload.uuid}.`,
          videoResource,
        );
        break;
      case 'missing-progressive-medium':
        warnAndNotify(
          'ProgressiveMediumRenderingFailure',
          `Medium progressive stream is missing from the video asset ${videoResource.payload.uuid}.`,
          videoResource,
        );
        break;
      case 'missing-progressive-high':
        warnAndNotify(
          'ProgressiveHighRenderingFailure',
          `High progressive stream is missing from the video asset ${videoResource.payload.uuid}.`,
          videoResource,
        );
        break;
      case 'missing-adaptive-sources':
        warnAndNotify(
          'AdaptivePlayerRenderingFailure',
          `Adaptive player applies for a user, but adaptive sources are missing from the video asset ${videoResource.payload.uuid}. Falling back to ProgressivePlayer.`,
          videoResource,
        );
        break;
      default:
        assertNever(failure);
    }
  };

/**
 * Video will take the user's context (browser and server) into account in
 * order to determine the proper video player to render, which will be either:
 *  - JWPlayer playing a progressive source,
 *  - JWPlayer playing an adaptvie source, falling back to a progressive source
 *
 * NoPlayer is rendered in the case that no appropriate player can be
 * determined by the user's context. This should not happen. The rendering of
 * NoPlayer will trigger an alert.
 */

function ReqHlsVideo(props: Props) {
  const [playerSelection, setPlayerSelection] = useState<SelectedPlayer>({
    kind: 'undetermined',
  });

  useEffect(() => {
    selectPlayer(window.navigator.userAgent, props.videoResource).fork(
      noop,
      ({ errors, result }) => {
        errors.forEach(logFailure(props.videoResource));
        setPlayerSelection(result);
      },
    );
  }, [props.videoResource]);

  const forwardProps = { ...props, selectedPlayer: playerSelection };
  const { playButton } = forwardProps.children;

  log(
    'Player kind',
    forwardProps.selectedPlayer.kind,
    'for',
    forwardProps.videoResource.payload.uuid,
  );

  return (
    <div data-test-video={props.id} className="w-full h-full flex">
      {playButton.getOrElseValue(<></>)}
      <ReqHlsRenderedPlayer {...forwardProps} />
    </div>
  );
}

export default observer(ReqHlsVideo);
